import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

import Button from '../../../components/ui/Button';

const PerformanceChart = () => {
  const [selectedMetric, setSelectedMetric] = useState('response_time');
  const [timeRange, setTimeRange] = useState('1h');

  const metrics = [
    { id: 'response_time', label: 'Response Time', unit: 'ms', color: '#3B82F6' },
    { id: 'throughput', label: 'Throughput', unit: 'req/s', color: '#10B981' },
    { id: 'error_rate', label: 'Error Rate', unit: '%', color: '#EF4444' },
    { id: 'cpu_usage', label: 'CPU Usage', unit: '%', color: '#F59E0B' }
  ];

  const timeRanges = [
    { value: '1h', label: '1 Hour' },
    { value: '6h', label: '6 Hours' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' }
  ];

  // Mock performance data with anomalies
  const performanceData = Array.from({ length: 60 }, (_, i) => {
    const timestamp = new Date(Date.now() - (59 - i) * 60000);
    const baseValue = selectedMetric === 'response_time' ? 120 : 
                     selectedMetric === 'throughput' ? 850 :
                     selectedMetric === 'error_rate' ? 0.5 : 45;
    
    // Add some anomalies
    const anomaly = i === 25 || i === 45 ? 2.5 : 1;
    const noise = (Math.random() - 0.5) * 0.2;
    
    return {
      timestamp: timestamp?.toLocaleTimeString('en-US', { hour12: false }),
      value: Math.max(0, baseValue * anomaly * (1 + noise)),
      isAnomaly: anomaly > 1
    };
  });

  const currentMetric = metrics?.find(m => m?.id === selectedMetric);
  const threshold = selectedMetric === 'response_time' ? 200 : 
                   selectedMetric === 'throughput' ? 500 :
                   selectedMetric === 'error_rate' ? 2 : 80;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 elevation-2">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-sm text-muted-foreground">
            {currentMetric?.label}: {payload?.[0]?.value?.toFixed(2)} {currentMetric?.unit}
          </p>
          {data?.isAnomaly && (
            <p className="text-xs text-error font-medium">⚠ Anomaly detected</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground">System Performance</h3>
          <p className="text-sm text-muted-foreground">Real-time monitoring with anomaly detection</p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Metric Selector */}
          <div className="flex bg-muted rounded-md p-1">
            {metrics?.map((metric) => (
              <button
                key={metric?.id}
                onClick={() => setSelectedMetric(metric?.id)}
                className={`px-3 py-1 text-xs font-medium rounded transition-hover ${
                  selectedMetric === metric?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {metric?.label}
              </button>
            ))}
          </div>

          {/* Time Range */}
          <div className="flex bg-muted rounded-md p-1">
            {timeRanges?.map((range) => (
              <button
                key={range?.value}
                onClick={() => setTimeRange(range?.value)}
                className={`px-2 py-1 text-xs font-medium rounded transition-hover ${
                  timeRange === range?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {range?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Chart */}
      <div className="h-80 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={performanceData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(148, 163, 184, 0.3)" />
            <XAxis 
              dataKey="timestamp" 
              stroke="rgb(148, 163, 184)"
              fontSize={12}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="rgb(148, 163, 184)"
              fontSize={12}
              label={{ 
                value: currentMetric?.unit, 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine 
              y={threshold} 
              stroke="rgb(239, 68, 68)" 
              strokeDasharray="5 5"
              label={{ value: "Threshold", position: "topRight" }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={currentMetric?.color}
              strokeWidth={2}
              dot={(props) => {
                const { payload } = props;
                return payload?.isAnomaly ? (
                  <circle 
                    cx={props?.cx} 
                    cy={props?.cy} 
                    r={4} 
                    fill="rgb(239, 68, 68)" 
                    stroke="rgb(239, 68, 68)"
                  />
                ) : null;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-0.5" style={{ backgroundColor: currentMetric?.color }}></div>
            <span>{currentMetric?.label}</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-0.5 bg-error"></div>
            <span>Threshold</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-error rounded-full"></div>
            <span>Anomaly</span>
          </div>
        </div>
        
        <Button variant="ghost" size="sm" iconName="ZoomIn">
          Zoom
        </Button>
      </div>
    </div>
  );
};

export default PerformanceChart;